<template>
	<Panel
		v-bind="panelObject"
		class="PanelVehicleExtra"
	>
		<template #header>
			<strong
				v-if="vinFailed"
				style="color: var(--error-color)"
				>The VIN could not be processed</strong
			>
			<div
				style="display: inline-flex"
				v-if="populatingVehicleDetails"
			>
				<strong
					class="vin-lookup-flash"
					style=""
					>Loading...</strong
				>
				<img
					class="loading-anim"
					src="@static/img/SaturnLogoAnim.svg"
					style="width: 40px; margin-right: 2%"
				/>
			</div>
		</template>
		<div ref="extraInfo">
			<div
				class="inputs-grid"
				:style="$grid('1-2-2')"
			>
				<InputNumber
					label="Year"
					class="glow"
					placeholder="Year"
					:thousandSeparator="''"
					v-model:saturn="fimenu.vehicle.modelYear"
					:invalid="util.isNull(validation) ? false : usedValidation.modelYear.$invalid"
					:maxlength="4"
					:disabled="(disabled && !overrideVinDetails) || populatingVehicleDetails"
					@change="yearChanged"
					@blur="saveYearAsInt"
				>
					<template #validation>
						<div v-if="!util.isNull(validation) && usedValidation.modelYear.required.$invalid">Year is required</div>
						<div v-else-if="!util.isNull(validation) && usedValidation.modelYear.validVehicleYear.$invalid">Year is invalid</div>
					</template>
				</InputNumber>
				<InputTextbox
					label="Style"
					v-model:saturn="fimenu.vehicle.style"
					:disabled="disabled"
					placeholder="Style"
					class="glow grid-3-column"
					v-show="!showExtra"
				>
				</InputTextbox>
				<InputRichDropdown
					v-if="showTextBox"
					v-model:saturn="fimenu.vehicle.make"
					label="Make"
					:class="{ 'grid-2-column': !showExtra }"
					:invalid="util.isNull(validation) ? false : usedValidation.make.$invalid"
					:list="allMakes"
					:valueMap="(make: any) => make.display"
					:display="(make: any) => make.display"
					:disabled="disabled || populatingVehicleDetails"
					:customHandling="customHandling"
					search
					@change="makeChanged(true)"
				>
					<template #validation>
						<div v-if="!util.isNull(validation) && usedValidation.make.required.$invalid">Make is required</div>
						<div v-else-if="!util.isNull(validation) && usedValidation.make.isValid.$invalid">
							{{ vehicleMakeError }}
						</div>
					</template>
				</InputRichDropdown>
				<InputRichDropdown
					v-else-if="!isBusy"
					label="Make"
					v-model:saturn="fimenu.vehicle.make"
					class="glow"
					:list="allMakes"
					:valueMap="(make: any) => make.display"
					:display="(make: any) => make.display"
					:invalid="util.isNull(validation) ? false : usedValidation.make.$invalid"
					:class="{ 'grid-2-column': !showExtra }"
					:disabled="(disabled && !releaseVinInfo) || populatingVehicleDetails"
					@change="makeChanged(false)"
				>
					<template #validation>
						<div v-if="!util.isNull(validation) && usedValidation.make.required.$invalid">Make is required</div>
						<div v-else-if="!util.isNull(validation) && usedValidation.make.isValid.$invalid">
							{{ vehicleMakeError }}
						</div>
					</template>
				</InputRichDropdown>
				<InputRichDropdown
					v-if="showTextBox"
					v-model:saturn="fimenu.vehicle.model"
					label="Model"
					:class="{ 'grid-2-column': !showExtra }"
					:invalid="util.isNull(validation) ? false : usedValidation.model.$invalid"
					:list="models"
					:disabled="(disabled && !releaseVinInfo) || populatingVehicleDetails"
					:customHandling="customHandling"
					search
					@change="modelChanged"
				>
					<template #validation>
						<div v-if="!util.isNull(validation) && usedValidation.model.required.$invalid">Model is required</div>
					</template>
				</InputRichDropdown>
				<InputRichDropdown
					v-else-if="!isBusy"
					label="Model"
					v-model:saturn="fimenu.vehicle.model"
					:list="models"
					class="glow"
					:invalid="util.isNull(validation) ? false : usedValidation.model.$invalid"
					:class="{ 'grid-2-column': !showExtra }"
					:disabled="(disabled && !releaseVinInfo) || populatingVehicleDetails"
					@change="modelChanged"
				>
					<template #validation>
						<div v-if="!util.isNull(validation) && usedValidation.model.required.$invalid">Model is required</div>
					</template>
				</InputRichDropdown>
				<InputTextbox
					label="Style"
					v-model:saturn="fimenu.vehicle.style"
					:disabled="(disabled || !releaseVinInfo) && !overrideVinDetails"
					placeholder="Style"
					class="glow grid-2-column"
					v-show="showExtra"
				>
				</InputTextbox>
				<InputTextbox
					label="Exterior Color"
					v-model:saturn="fimenu.vehicle.exteriorColor"
					placeholder="Exterior Color"
					:invalid="util.isNull(validation) ? false : usedValidation.exteriorColor.$invalid"
					v-if="showExtra"
				>
					<template #validation>
						<div v-if="!util.isNull(validation) && usedValidation.exteriorColor.required.$invalid">Exterior Color is required</div>
					</template>
				</InputTextbox>
			</div>
			<template v-if="showExtra">
				<div :style="$grid('1-1-1-1-1')">
					<InputRichDropdown
						label="Vehicle Type"
						v-model:saturn="fimenu.vehicle.primaryBodyType"
						:list="bodyTypes"
						class="glow"
						:valueMap="(type: any) => type.code"
						:display="(type: any) => type.description"
						:invalid="util.isNull(validation) ? false : usedValidation.primaryBodyType.$invalid"
						nothingSelected="-- Select Value --"
						search
					>
						<template #validation>
							<div v-if="!util.isNull(validation) && usedValidation.primaryBodyType.required.$invalid">Vehicle Type is required</div>
						</template>
					</InputRichDropdown>
					<InputRichDropdown
						label="Fuel Type"
						v-model:saturn="fimenu.vehicle.fuelType"
						:list="fuelTypes"
						class="glow"
						:valueMap="(oil: any) => oil.code"
						:display="(oil: any) => oil.description"
						:invalid="util.isNull(validation) ? false : usedValidation.fuelType.$invalid"
						nothingSelected="-- Select Value --"
						search
					>
						<template #validation>
							<div v-if="!util.isNull(validation) && usedValidation.fuelType.required.$invalid">Fuel Type is required</div>
						</template>
					</InputRichDropdown>
					<InputRichDropdown
						label="Oil Type"
						nothingSelected="-- Select Value --"
						v-model:saturn="fimenu.vehicleOil"
						:list="oilTypes"
						:valueMap="(o: any) => o.value"
						:display="(o: any) => o.display"
						:invalid="util.isNull(usedExtraValidation) ? false : usedExtraValidation.vehicleOil.$invalid"
						:disabled="fimenu.vehicle.fuelType == fuelTypes.find(ft => ft.description == 'Electric').code"
						@change="oilTypeChangeHandle"
						search
					>
						<template #validation>
							<div v-if="!util.isNull(usedExtraValidation) && usedExtraValidation.vehicleOil.required.$invalid">Oil Type is required</div>
						</template>
					</InputRichDropdown>
					<InputCurrency
						v-if="!fimenu.buyersOrderEnabled && msrp"
						:label="fimenu.inventoryType == 'New' ? 'MSRP' : 'Retail'"
						v-model:saturn="msrp.retail"
						class="grid-2-column"
						:placeholder="fimenu.inventoryType == 'New' ? 'MSRP' : 'Retail'"
						:invalid="util.isNull(validation) ? false : usedExtraValidation.vehicle.bookValues[ManufacturerIndex]?.retail.$invalid"
						iconSide="left"
						@blur="e => checkReprice(e.target.value, setWearPricing)"
					>
						<template #validation>
							<div v-if="!util.isNull(validation) && usedExtraValidation.vehicle.bookValues[ManufacturerIndex]?.retail.required.$invalid">
								{{ fimenu.inventoryType == 'New' ? 'MSRP' : 'Retail' }} is required
							</div>
							<div v-else>{{ fimenu.inventoryType == 'New' ? 'MSRP' : 'Retail' }} Is in valid</div>
						</template>
					</InputCurrency>

					<!-- FIXME -->
					<!-- Add proper grid and remove placeholder div. -->
					<div
						v-else
						class="grid-2-column"
					/>

					<InputTextbox
						label="Title Number"
						v-model:saturn="fimenu.vehicle.titleNumber"
						placeholder="Title Number"
						v-if="showExtra"
					>
					</InputTextbox>
					<InputRichDropdown
						label="Title State"
						class="grid-2-column"
						v-model:saturn="fimenu.vehicle.titleState"
						:list="states"
						:valueMap="(p: any) => p.code"
						:display="(p: any) => p.description"
						v-if="showExtra"
						search
					>
					</InputRichDropdown>
					<div
						v-if="showExtra"
						class="radio-button-inline no-padding-grid grid-2-column"
					>
						<span>Electronic Title?</span>
						<div class="no-padding-grid gap">
							<InputRadio
								label="Yes"
								:valueMatch="true"
								v-model="fimenu.vehicle.isElectronicTitle"
								:isButton="true"
							></InputRadio>
							<InputRadio
								label="No"
								:valueMatch="false"
								v-model="fimenu.vehicle.isElectronicTitle"
								:isButton="true"
							></InputRadio>
						</div>
					</div>

					<InputRichDropdown
						label="Mileage Status"
						v-model:saturn="fimenu.vehicle.mileageStatus"
						:list="VEHICLE_MILEAGE_STATUS.toList()"
						class="glow grid-3-column"
						:valueMap="(status: any) => status.value"
						:display="(status: any) => status.display"
						:invalid="util.isNull(validation) ? false : usedValidation.mileageStatus.$invalid"
						nothingSelected="-- Select Value --"
					>
						<template #validation>
							<div v-if="!util.isNull(validation) && usedValidation.mileageStatus.required.$invalid">Mileage Status is required</div>
						</template>
					</InputRichDropdown>

					<div
						class="radio-button-inline no-padding-grid grid-2-column"
						:invalid="util.isNull(validation) ? false : usedValidation.hasFiveDigitOdometer.$invalid"
					>
						<span>Five Digit Odometer?</span>
						<div class="no-padding-grid gap">
							<InputRadio
								label="Yes"
								:valueMatch="true"
								v-model="fimenu.vehicle.hasFiveDigitOdometer"
								:isButton="true"
							></InputRadio>
							<InputRadio
								label="No"
								:valueMatch="false"
								v-model="fimenu.vehicle.hasFiveDigitOdometer"
								:isButton="true"
							></InputRadio>
						</div>
					</div>
				</div>
				<div :style="$grid('4-4-4')">
					<InputTextbox
						v-for="info in otherVehicleInfo"
						:key="info.key"
						:label="info.display"
						v-model:saturn="(fimenu.vehicle.otherVehicleInfo as any)[info.key]"
						:invalid="
							util.isNull(validation)
								? false
								: !util.isNull(usedExtraValidation.vehicle.otherVehicleInfo[info.key])
									? usedExtraValidation.vehicle.otherVehicleInfo[info.key].$invalid
									: false
						"
						:placeholder="info.display"
						@change="value => validateIfIsNull(info.key, value)"
						class="glow"
					>
						<template #validation>
							<div
								v-if="
									!util.isNull(validation) &&
									!util.isNull(usedExtraValidation.vehicle.otherVehicleInfo[info.key]) &&
									!usedExtraValidation.vehicle.otherVehicleInfo[info.key].required
								"
							>
								Value Required
							</div>
							<div
								v-else-if="
									!util.isNull(validation) &&
									!util.isNull(usedExtraValidation.vehicle.otherVehicleInfo[info.key]) &&
									!usedExtraValidation.vehicle.otherVehicleInfo[info.key].numeric
								"
							>
								Field must be a number
							</div>
						</template>
					</InputTextbox>
					<InputTextbox
						label="Drive Train"
						v-model:saturn="DriveTrain[fimenu.vehicle.driveTrain]"
						placeholder="Drive Train"
						@change="value => validateIfIsNull(fimenu.vehicle.driveTrain, value)"
						class="glow"
					>
					</InputTextbox>
				</div>
			</template>
		</div>
	</Panel>
</template>
<script lang="ts">
	export interface IPanelVehicleExtraProps {
		fimenu?: FIMenu;
		panelObject?: any;
		validation?: any;
		validationExtra?: any;
		showExtra?: any;
		copiedVehicleExtraInfo?: any;
		oilTypeChangeAfterPricing?: any;
		oilTypeNotAvailable?: any;
		setWearPricing?: any;
		customVin?: any;
		refreshCache?: any;
		populatingVehicleDetails?: any;
		swapDropdown?: any;
		classOverride?: any;
		allowVinEdit?: any;
		disabled?: any;
		vinFailed?: any;
		overrideVinDetails?: any;
	}
</script>
<script setup lang="ts">
	import { bodyTypes, fuelTypes, OIL_TYPES, oilTypes, otherVehicleInfo, states, VEHICLE_MILEAGE_STATUS } from '@core/classes/Enums';
	import { BookValue, DriveTrain } from '@core/classes/SharedEnums';
	import { computed, inject, onMounted, Ref, ref, useTemplateRef, watch } from 'vue';
	import DropdownHelper from '@core/helpers/dropdown-helper';
	import FIMenu from '@core/classes/FIMenu';
	import FIMenuOtherVehicleInfo from '@core/classes/FIMenuOtherVehicleInfo';
	import InputCurrency from '@core/components/InputCurrency.vue';
	import InputNumber from '@core/components/InputNumber.vue';
	import InputRadio from '@core/components/InputRadio.vue';
	import InputRichDropdown from '@core/components/InputRichDropdown.vue';
	import InputTextbox from '@core/components/InputTextbox.vue';
	import Panel from '@core/components/Panel.vue';
	import util from '@core/services/util';

	const {
		fimenu,
		panelObject,
		validation,
		validationExtra,
		showExtra,
		copiedVehicleExtraInfo,
		oilTypeChangeAfterPricing,
		oilTypeNotAvailable,
		setWearPricing,
		customVin,
		refreshCache,
		populatingVehicleDetails,
		swapDropdown,
		classOverride,
		allowVinEdit,
		disabled,
		vinFailed,
		overrideVinDetails,
	} = defineProps<IPanelVehicleExtraProps>();
	const emits = defineEmits(['change']);

	const dealMakeId = inject<Ref<string>>('dealMakeId', null);

	const extraInfoRef = useTemplateRef('extraInfo');

	const isBusy = ref(false);
	const oilTypeActual = ref(null);
	const currentOilTypes = ref([]);
	const TEST = {
		vt: 'SUV',
		ft: 'Gas',
		ln: '',
		lna: 1000,
		wr: 11,
	};

	const previousMSRP = ref(fimenu.getMSRPOrHighestOrDefaultRetailBookValue()?.retail ?? fimenu.getMSRP());
	const allMakes = ref([]);
	const allModels = ref([]);
	const releaseVinInfo = ref(false);

	const usedValidation = computed(() => (showExtra ? validation.vehicle : validation));

	const usedExtraValidation = computed(() => (showExtra ? validation : null));

	const models = computed(() => {
		return [...new Set(allModels.value.filter(m => m.value.saturnBrandId == fimenu.vehicle.makeId).map(m => m.display))];
	});

	const ignoreValidLength = computed(() => (fimenu.vin ? true : false));

	const vehicleMakeError = computed(() => {
		const message = 'Store does not sell this make';
		if (!fimenu.inventoryType) {
			return 'You must specify if this vehicle is New/Used/CPO';
		}
		if (fimenu.inventoryType == 'Used' && fimenu.vehicleWarranty.isCertified) {
			return `${message} as CPO`;
		} else {
			return `${message} as ${fimenu.inventoryType}`;
		}
	});

	const showTextBox = computed(() => {
		if (overrideVinDetails) {
			return true;
		}
		return swapDropdown ?? false;
	});

	const msrp = computed(() => {
		if (!fimenu.buyersOrderEnabled) return fimenu.vehicle.getOrCreateManufacturer();
		else return null;
	});

	const ManufacturerIndex = computed(() => fimenu.vehicle.bookValues.findIndex(x => x.source == BookValue.Manufacturer));

	onMounted(() => {
		isBusy.value = true;

		getMakesAndModels();

		isBusy.value = false;

		watch(
			() => fimenu.vehicle.fuelType,
			() => {
				fimenu.vehicleOil = null;
				if (fimenu.vehicle.fuelType == fuelTypes.find(ft => ft.description == 'Electric').code) {
					fimenu.vehicleOil = OIL_TYPES.NA;
				}
				setOilTypeList();
			},
		);

		watch(
			() => fimenu.vehicle.modelYear,
			() => {
				yearChanged();
			},
		);

		if (util.isNull(fimenu.vehicle.hasFiveDigitOdometer) && fimenu.vehicle.modelYear >= 2000) {
			fimenu.vehicle.hasFiveDigitOdometer = false;
		}

		oilTypeActual.value = fimenu.vehicleOil;

		setOilTypeList();
		if (util.isNull(fimenu.vehicle.isElectronicTitle)) {
			fimenu.vehicle.isElectronicTitle = true;
		}
	});

	watch(
		() => fimenu.vehicle.make,
		() => {
			makeChanged();
		},
	);

	watch(
		() => refreshCache,
		() => {
			if (refreshCache === true) {
				getMakesAndModels(true);
			}
		},
	);

	function customHandling(value: string) {
		return {
			display: value,
			obj: {
				brandId: -1,
				brandName: value,
				saturnBrandId: null as string,
			},
		};
	}

	function yearChanged() {
		if (fimenu.vehicle.modelYear < 2000) {
			fimenu.vehicle.hasFiveDigitOdometer = null;
		} else {
			fimenu.vehicle.hasFiveDigitOdometer = false;
		}
	}

	function makeChanged(clearModel = false) {
		if (clearModel) {
			fimenu.vehicle.model = null;
			fimenu.vehicle.modelId = null;
		}

		if (fimenu.vehicle.make && allMakes.value.length > 0) {
			fimenu.vehicle.makeId = allMakes.value.find(m => m.display.toLowerCase() == fimenu.vehicle.make.toLowerCase())?.value.saturnBrandId;
			dealMakeId.value = fimenu.vehicle.makeId;
		} else {
			fimenu.vehicle.makeId = null;
			dealMakeId.value = null;
		}
	}

	function validateIfIsNull(property: any, value: any) {
		if (util.isNullOrEmpty(value, true)) {
			delete fimenu.vehicle.otherVehicleInfo[property as keyof FIMenuOtherVehicleInfo];
		}
	}

	function modelChanged() {
		if (fimenu.vehicle.model) {
			fimenu.vehicle.modelId = allModels.value.find(m => m.display == fimenu.vehicle.model)?.value.modelId;
		} else {
			fimenu.vehicle.modelId = null;
		}
	}

	function saveYearAsInt(e: any) {
		if (!e.target.value) fimenu.vehicle.modelYear = null;
		else fimenu.vehicle.modelYear = parseInt(e.target.value);
	}

	function oilTypeChangeHandle() {
		if (oilTypeActual.value != fimenu.vehicleOil) {
			oilTypeActual.value = fimenu.vehicleOil;
			oilTypeChangeAfterPricing();
		}
	}

	function setOilTypeList() {
		if (fimenu.vehicle.fuelType == fuelTypes.find(ft => ft.description == 'Diesel').code) {
			currentOilTypes.value = oilTypes.map(o => ({
				...o,
				disabled: o.value !== OIL_TYPES.DIESEL,
			}));
		} else {
			currentOilTypes.value = oilTypes.map(o => ({
				...o,
				disabled: o.value === OIL_TYPES.NA,
			}));
		}
	}

	function checkReprice(newValue: any, repriceFunc: any) {
		if (newValue != null && newValue != '' && previousMSRP.value != parseInt(newValue)) {
			previousMSRP.value = newValue;
			repriceFunc();
		}
	}

	function clearField() {
		releaseVinInfo.value = true;
		fimenu.vehicle.make = null;
		fimenu.vehicle.model = null;
		fimenu.vehicle.modelYear = null;
		fimenu.vehicle.style = null;
	}

	function getMakesAndModels(forceRefresh = false) {
		DropdownHelper.getBrands(forceRefresh).then(response => {
			allMakes.value = response;
			if (!fimenu.vehicle.makeId) {
				makeChanged();
			}
		});
		DropdownHelper.getModels(forceRefresh).then(response => {
			allModels.value = response;
		});
	}

	defineExpose({
		extraInfoRef,
	});
</script>

<style scoped>
	.PanelVehicleExtra .radio-button-inline {
		width: 80%;
		justify-content: space-between;
	}

	.vin-lookup-flash {
		animation: blinking 1s infinite;
		margin-top: auto;
		margin-bottom: auto;
		margin-right: 2px;
		color: var(--main-color);
		font-size: larger;
	}

	.flash-green {
		background: var(--success-color);
		animation: blinking 1s infinite;
	}
</style>
